<template>
  <div class="space-y-4">
    <h2>{{ t('title') }}</h2>
    <p class="text-sm text-obsidian-500">{{ t('description') }}</p>

    <div class="space-y-4 rounded-md bg-white pb-4 pl-4 pr-4 pt-1">
      <div v-if="!fields.length" class="flex justify-center p-4">
        {{ t('empty') }}
      </div>

      <div
        v-for="(field, i) in fields"
        :key="field.key"
        class="relative flex items-end gap-4 border-b pb-3 pt-3 last:border-b-0 last:pb-0"
      >
        <div class="grid flex-1 grid-cols-2 gap-4">
          <InputField :name="`areaChargePrices[${i}].name`" :label="t('name')" type="text" />
          <InputField
            :name="`areaChargePrices[${i}].amount`"
            :label="t('amount')"
            type="number"
            :min="0"
            :max-fraction-digits="2"
            adornment="€"
          />
        </div>
        <button class="px-2 py-3 text-bittersweet-600" @click.prevent="removeItem(i)">
          <XIcon class="size-4" />
        </button>
      </div>

      <InputHint :hint="error" is-error />

      <div class="flex justify-center">
        <CVButton variant="ghost" @click.prevent="addItem">{{ t('add') }}</CVButton>
      </div>
    </div>

    <div class="flex justify-end">
      <CVButton :is-loading="isSaving" @click="handleSave">{{ t('save') }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { AreaChargePriceDto } from '@carvia/ros-client-types';
import { XIcon } from 'lucide-vue-next';
import { useFieldArray, useFieldError, useForm } from 'vee-validate';
import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import CVButton from '@/components/buttons/CVButton.vue';
import InputField from '@/components/InputField.vue';
import InputHint from '@/components/InputHint.vue';

const props = defineProps<{
  prices: AreaChargePriceDto[];
  isSaving?: boolean;
}>();

const emit = defineEmits<{
  save: [prices: AreaChargePriceDto[]];
}>();

const { t } = useI18n();

const copyPrices = (prices: AreaChargePriceDto[]) => prices.map((price) => ({ ...price }));

const { handleSubmit, meta, resetForm } = useForm({
  initialValues: { areaChargePrices: copyPrices(props.prices) },
  validationSchema: yup.object({
    areaChargePrices: yup.array(
      yup.object({
        id: yup.string().required(),
        name: yup.string().trim().required(),
        amount: yup.number().min(0).required(),
      }),
    ),
  }),
});

const { fields, push, remove } = useFieldArray<AreaChargePriceDto>('areaChargePrices');
const error = useFieldError('areaChargePrices');

watch(
  () => props.prices,
  (prices) => {
    if (meta.value.dirty) return;
    resetForm({ values: { areaChargePrices: copyPrices(prices) } });
  },
);

const addItem = () => {
  push({ id: uuidv4(), name: '', amount: 0 });
};

const removeItem = (index: number) => {
  remove(index);
};

const handleSave = handleSubmit((values) => {
  const prices = values.areaChargePrices ?? [];
  emit('save', prices);
  resetForm({ values: { areaChargePrices: copyPrices(prices) } });
});
</script>

<i18n lang="json">
{
  "en": {
    "title": "Prices",
    "description": "Create a named price, for example Airport at €80 or Ferry at €200. Areas use one of these prices. Changing the amount updates every area that uses it.",
    "empty": "No prices yet.",
    "name": "Name",
    "amount": "Surcharge",
    "add": "Add price",
    "save": "Save prices"
  },
  "de": {
    "title": "Preise",
    "description": "Lege einen Preis mit Namen an, zum Beispiel Flughafen 80 € oder Fähre 200 €. Gebiete verwenden einen dieser Preise. Eine Änderung gilt für alle Gebiete mit diesem Preis.",
    "empty": "Noch keine Preise.",
    "name": "Name",
    "amount": "Aufschlag",
    "add": "Preis anlegen",
    "save": "Preise speichern"
  },
  "es": {
    "title": "Precios",
    "description": "Crea un precio con nombre, por ejemplo Aeropuerto 80 € o Ferry 200 €. Las zonas usan uno de estos precios. Un cambio vale para todas las zonas con ese precio.",
    "empty": "Todavía no hay precios.",
    "name": "Nombre",
    "amount": "Recargo",
    "add": "Añadir precio",
    "save": "Guardar precios"
  }
}
</i18n>
